import { useQuickCheck } from '@/hooks/useQuickCheck';
import type { QuickCheck as QuickCheckSpec } from '@/types';

interface QuickCheckProps {
  ticketId: string;
  qc: QuickCheckSpec;
}

/** Multi-choice Quick Check — replaces legacy qcReveal IIFE per A.2.1.
 *  H.4-EXT4.2 (17 MAY 2026 slot_riso_phase_h_final_smoke):
 *  «Не совсем — открой разбор ниже» feedback + «заново» retry button removed
 *  per Genie round 4 pick. On wrong pick, «Правильный ответ» details block
 *  auto-opens (existing behavior) · no retry loop required. Student sees
 *  correct answer directly after any submit · close-ticket gate dropped
 *  separately в Ticket.tsx (no longer requires quickCheckPassed). */
export function QuickCheck({ ticketId, qc }: QuickCheckProps) {
  const { state, selectedOption, submit } = useQuickCheck(ticketId, qc);

  const isPristine = state === 'pristine';
  return (
    <div className="practice-inline" role="group" aria-labelledby={`qc-${qc.id}-prompt`}>
      <div className="h" id={`qc-${qc.id}-prompt`}>
        {qc.prompt}
      </div>
      <div className="desc" dangerouslySetInnerHTML={{ __html: qc.question }} />
      <div className="qc-options" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
        {qc.options.map((opt) => {
          const isSelected = selectedOption === opt.id;
          const isCorrect = !isPristine && opt.id === qc.answer;
          const isWrongPick = state === 'wrong' && isSelected;
          return (
            <button
              key={opt.id}
              type="button"
              className="qc-opt"
              onClick={() => isPristine && submit(opt.id)}
              disabled={!isPristine}
              aria-pressed={isSelected}
              style={{
                textAlign: 'left',
                padding: '10px 14px',
                borderRadius: 6,
                border: `1.5px solid ${
                  isCorrect
                    ? 'var(--green)'
                    : isWrongPick
                      ? 'var(--accent)'
                      : 'var(--border-strong)'
                }`,
                background: isCorrect
                  ? 'var(--green-soft)'
                  : isWrongPick
                    ? 'var(--accent-soft)'
                    : 'var(--surface)',
                fontFamily: 'Inter, sans-serif',
                fontStyle: 'italic',
                fontSize: '13.5px',
                lineHeight: 1.55,
                color: 'var(--ink)',
                cursor: isPristine ? 'pointer' : 'default',
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: opt.text }} />
              {isCorrect && <span style={{ marginLeft: 8 }}>✓</span>}
            </button>
          );
        })}
      </div>
      {!isPristine && (() => {
        const correctOpt = qc.options.find((o) => o.id === qc.answer);
        return (
          <>
            {/* H.4-EXT4.2 (17 MAY 2026): wrong-state feedback line + retry
                button removed. Correct-state shows ✓ Зачтено positive cue.
                On wrong pick details block (below) auto-opens с «Правильный
                ответ» · no retry loop · ticket closable independently. */}
            {state === 'correct' && (
              <div className="feedback ok">✓ Зачтено</div>
            )}
            <details
              className="qc-explain"
              data-tour="quick-check-explain"
              open={state === 'wrong'}
              style={{
                marginTop: 10,
                background: 'var(--surface-2)',
                border: '1px dashed var(--border-strong)',
                borderRadius: 'var(--r-md)',
                padding: '10px 14px',
              }}
            >
              <summary
                style={{
                  cursor: 'pointer',
                  fontFamily: 'Space Mono, monospace',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 700,
                  color: 'var(--ink-soft)',
                  listStyle: 'none',
                }}
              >
                Проверить ответ
              </summary>
              <div style={{ marginTop: 10, fontFamily: 'Inter, sans-serif', fontSize: 13.5, lineHeight: 1.55 }}>
                <div style={{ marginBottom: 6 }}>
                  <strong>Правильный ответ:</strong>{' '}
                  <span
                    style={{ color: 'var(--green)' }}
                    dangerouslySetInnerHTML={{ __html: correctOpt?.text ?? qc.answer }}
                  />
                </div>
                <div
                  style={{ color: 'var(--ink-soft)' }}
                  dangerouslySetInnerHTML={{ __html: qc.explanation }}
                />
              </div>
            </details>
          </>
        );
      })()}
    </div>
  );
}
